import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import * as Select from '@radix-ui/react-select';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import PublicHeader from '../../../components/ui/PublicHeader';
import PublicFooter from '../../../components/ui/PublicFooter';
import publicJobService from '../../../services/publicJobService';

const CustomSelect = ({ value, onValueChange, placeholder, options }) => (
  <Select.Root value={value} onValueChange={onValueChange}>
    <Select.Trigger className="flex items-center justify-between w-full bg-white/10 hover:bg-white/15 border-0 text-white text-sm rounded-2xl py-3 px-4 focus:ring-1 focus:ring-white/20 transition-all cursor-pointer outline-none">
      <Select.Value placeholder={placeholder} />
      <Select.Icon>
        <Icon name="ChevronDown" className="h-4 w-4 text-gray-400" />
      </Select.Icon>
    </Select.Trigger>

    <Select.Portal>
      <Select.Content className="z-[100] overflow-hidden bg-[#003459] border border-white/10 rounded-2xl shadow-2xl animate-in fade-in zoom-in-95 duration-200">
        <Select.ScrollUpButton className="flex items-center justify-center h-8 bg-[#1a1a1a] cursor-default">
          <Icon name="ChevronUp" className="h-4 w-4 text-white" />
        </Select.ScrollUpButton>
        <Select.Viewport className="p-2">
          <Select.Item value="all" className="relative flex items-center h-10 px-8 rounded-xl text-sm text-gray-400 hover:text-white hover:bg-white/10 cursor-pointer outline-none transition-colors data-[highlighted]:bg-white/10 data-[highlighted]:text-white">
            <Select.ItemText>{placeholder}</Select.ItemText>
            <Select.ItemIndicator className="absolute left-2 inline-flex items-center">
              <Icon name="Check" className="h-4 w-4 text-indigo-400" />
            </Select.ItemIndicator>
          </Select.Item>
          {options.map((opt) => (
            <Select.Item
              key={opt}
              value={opt}
              className="relative flex items-center h-10 px-8 rounded-xl text-sm text-white hover:bg-white/10 cursor-pointer outline-none transition-colors data-[highlighted]:bg-white/10 data-[highlighted]:text-white"
            >
              <Select.ItemText>{opt.replace('-', ' ')}</Select.ItemText>
              <Select.ItemIndicator className="absolute left-2 inline-flex items-center">
                <Icon name="Check" className="h-4 w-4 text-indigo-400" />
              </Select.ItemIndicator>
            </Select.Item>
          ))}
        </Select.Viewport>
        <Select.ScrollDownButton className="flex items-center justify-center h-8 bg-[#1a1a1a] cursor-default">
          <Icon name="ChevronDown" className="h-4 w-4 text-white" />
        </Select.ScrollDownButton>
      </Select.Content>
    </Select.Portal>
  </Select.Root>
);

const PublicJobPortal = () => {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    department: '',
    location: '',
    employment_type: '',
    experience_level: '',
    remote_work_allowed: ''
  });
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  // Debounce search term to avoid too many API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Load jobs when filters or search term change
  useEffect(() => {
    loadPublicJobs();
  }, [debouncedSearchTerm, filters]);

  const loadPublicJobs = async () => {
    try {
      setLoading(true);
      const response = await publicJobService.getPublicJobs({
        status: 'active',
        page: 1,
        pageSize: 50,
        search: debouncedSearchTerm || undefined,
        department: filters.department || undefined,
        location: filters.location || undefined,
        employment_type: filters.employment_type || undefined,
        experience_level: filters.experience_level || undefined,
        remote_work_allowed: filters.remote_work_allowed ? (filters.remote_work_allowed === 'yes' ? true : false) : undefined
      });
      setJobs(response.jobs || []);
    } catch (error) {
      console.error('Error loading jobs:', error);
      setJobs([]);
    } finally {
      setLoading(false);
    }
  };

  const formatSalary = (min, max, currency = 'USD') => {
    if (!min && !max) return 'Salary not specified';
    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });

    if (min && max) return `${formatter.format(min)} - ${formatter.format(max)}`;
    if (min) return `${formatter.format(min)}+`;
    return `Up to ${formatter.format(max)}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Ongoing';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric'
    });
  };

  const Badge = ({ children, colorClass }) => (
    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold tracking-wider uppercase bg-opacity-10 ${colorClass}`}>
      {children}
    </span>
  );

  const getExperienceBadge = (level) => {
    const configs = {
      entry: { color: 'bg-emerald-500 text-emerald-600', label: 'Entry Level' },
      mid: { color: 'bg-blue-500 text-blue-600', label: 'Mid Level' },
      senior: { color: 'bg-purple-500 text-purple-600', label: 'Senior' },
      lead: { color: 'bg-orange-500 text-orange-600', label: 'Lead' },
      executive: { color: 'bg-rose-500 text-rose-600', label: 'Executive' }
    };
    const config = configs[level] || configs.mid;
    return <Badge colorClass={config.color}>{config.label}</Badge>;
  };

  const getEmploymentBadge = (type) => {
    const configs = {
      'full-time': { color: 'bg-indigo-500 text-indigo-600', label: 'Full-time' },
      'part-time': { color: 'bg-cyan-500 text-cyan-600', label: 'Part-time' },
      contract: { color: 'bg-amber-500 text-amber-600', label: 'Contract' },
      internship: { color: 'bg-violet-500 text-violet-600', label: 'Internship' }
    };
    const config = configs[type] || configs['full-time'];
    return <Badge colorClass={config.color}>{config.label}</Badge>;
  };

  return (
    <div className="min-h-screen bg-[#fafafa] selection:bg-indigo-100 selection:text-indigo-900">
      <PublicHeader />

      {/* Hero Section - Dark & Modern */}
      <section className="relative overflow-hidden bg-[#003459] py-24 sm:py-32">
        <div className="absolute top-0 right-0 -translate-y-1/2 translate-x-1/2 blur-3xl opacity-10 pointer-events-none">
          <div className="aspect-square w-[60rem] bg-gradient-to-br from-indigo-500 to-blue-500 rounded-full"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-4xl font-extrabold tracking-tight text-white sm:text-6xl mb-6 leading-tight">
              Scale Your Career with <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-indigo-400">Precision AI</span>
            </h1>
            <p className="text-lg leading-8 text-gray-300 mb-10 max-w-2xl">
              Connect with high-growth opportunities that align with your unique skill profile. Our AI-driven engine maps your potential to the world's most innovative teams.
            </p>
          </div>

          {/* Integrated Search & Filters bar */}
          <div className="relative mt-12 p-2 bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl shadow-2xl">
            <div className="flex flex-col lg:flex-row items-stretch gap-2">
              <div className="relative flex-1 group">
                <Icon name="Search" className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 group-focus-within:text-white transition-colors" />
                <input
                  type="text"
                  placeholder="Search roles, skills, or companies..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full bg-white/10 border-0 focus:ring-0 text-white placeholder-gray-500 py-4 pl-12 pr-4 rounded-2xl transition-all hover:bg-white/15 focus:bg-white/20"
                />
              </div>

              <div className="h-px lg:h-8 lg:w-px bg-white/10 self-center mx-2 hidden lg:block"></div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 lg:flex-1">
                {[
                  { key: 'department', label: 'All Fields', options: ['Engineering', 'Product', 'Design', 'Marketing', 'Sales', 'HR'] },
                  { key: 'location', label: 'Location', options: ['Remote', 'San Francisco', 'New York', 'London', 'Berlin'] },
                  { key: 'employment_type', label: 'Job Type', options: ['full-time', 'part-time', 'contract', 'internship'] },
                  { key: 'experience_level', label: 'Level', options: ['entry', 'mid', 'senior', 'lead', 'executive'] }
                ].map((filter) => (
                  <CustomSelect
                    key={filter.key}
                    value={filters[filter.key]}
                    onValueChange={(val) => setFilters({ ...filters, [filter.key]: val === 'all' ? '' : val })}
                    placeholder={filter.label}
                    options={filter.options}
                  />
                ))}
              </div>

              <div className="h-px lg:h-8 lg:w-px bg-white/10 self-center mx-2 hidden lg:block"></div>

              <div className="flex items-center px-4 bg-white/10 rounded-2xl hover:bg-white/15 transition-colors min-h-[52px]">
                <label className="flex items-center cursor-pointer gap-3 text-sm text-gray-300">
                  <input
                    type="checkbox"
                    checked={filters.remote_work_allowed === 'yes'}
                    onChange={(e) => setFilters({ ...filters, remote_work_allowed: e.target.checked ? 'yes' : '' })}
                    className="w-4 h-4 rounded border-white/20 bg-transparent text-indigo-500 focus:ring-indigo-500/50"
                  />
                  Remote Friendly
                </label>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content Area */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
            <div>
              <h2 className="text-3xl font-bold tracking-tight text-gray-900 mb-2">Available Opportunities</h2>
              <p className="text-gray-500">
                {loading ? 'Curating positions for you...' : (
                  <span className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
                    {jobs.length} roles found matching your criteria
                  </span>
                )}
              </p>
            </div>
          </div>

          {loading ? (
            <div className="grid gap-6">
              {[1, 2, 3].map(i => (
                <div key={i} className="h-48 bg-white border border-gray-100 rounded-3xl animate-pulse"></div>
              ))}
            </div>
          ) : jobs.length === 0 ? (
            <div className="text-center py-32 bg-white rounded-[3rem] border border-dashed border-gray-200">
              <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-6">
                <Icon name="SearchX" size={32} className="text-gray-400" />
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-2">No roles found</h4>
              <p className="text-gray-500 max-w-sm mx-auto">Try broadening your search or adjusting your filters.</p>
            </div>
          ) : (
            <div className="grid gap-6">
              {jobs.map((job) => (
                <div
                  key={job.id}
                  className="group relative bg-white border border-gray-100 p-8 rounded-[2rem] shadow-sm transition-all duration-300 hover:shadow-xl hover:-translate-y-1 hover:border-indigo-100"
                >
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-8">
                    <div className="flex-1">
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h3 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-indigo-600 transition-colors">
                            {job.title}
                          </h3>
                          <div className="flex flex-wrap items-center gap-6 text-sm font-medium text-gray-500">
                            <div className="flex items-center gap-2">
                              <Icon name="Building" size={18} className="text-gray-400" />
                              {job.department}
                            </div>
                            <div className="flex items-center gap-2">
                              <Icon name="MapPin" size={18} className="text-gray-400" />
                              {job.location}
                            </div>
                            {job.remote_work_allowed && (
                              <div className="flex items-center gap-2 text-indigo-600 font-bold">
                                <Icon name="Wifi" size={18} />
                                Remote Friendly
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-2">
                          {getEmploymentBadge(job.employment_type)}
                          {getExperienceBadge(job.experience_level)}
                        </div>
                      </div>

                      <p className="text-gray-600 mb-8 max-w-4xl line-clamp-2 text-lg">
                        {job.description}
                      </p>

                      <div className="flex flex-wrap items-center gap-8 text-sm text-gray-500 border-t border-gray-50 pt-6">
                        {(job.salary_range_min || job.salary_range_max) && (
                          <div className="flex items-center gap-2">
                            <Icon name="CircleDollarSign" size={18} className="text-emerald-500" />
                            <span className="font-bold text-gray-900">{formatSalary(job.salary_range_min, job.salary_range_max, job.currency)}</span>
                          </div>
                        )}

                        <div className="flex items-center gap-2">
                          <Icon name="Clock" size={18} className="text-gray-400" />
                          Posted {formatDate(job.created_at)}
                        </div>

                        {job.application_deadline && (
                          <div className="flex items-center gap-2 px-3 py-1 bg-rose-50 text-rose-600 rounded-lg font-bold">
                            <Icon name="Timer" size={16} />
                            Ends {formatDate(job.application_deadline)}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex-shrink-0">
                      <Button
                        variant="default"
                        onClick={() => navigate(`/public/jobs/${job.id}/apply`)}
                        className="w-full lg:w-48 bg-indigo-600 hover:bg-indigo-700 text-white rounded-2xl py-4 font-bold shadow-lg shadow-indigo-500/20 active:scale-95 transition-all"
                      >
                        Apply for Role
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <PublicFooter />
    </div>
  );
};

export default PublicJobPortal;
